import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { type FileRejection, type FileWithPath, useDropzone } from 'react-dropzone';

import { useTrackCompletedOnboardingUpload, useTrackViewedOnboardingUpload } from '@air/analytics';
import { type Board } from '@air/api/types';
import { Uploads } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { useAccount } from '@air/utils-auth';
import { cn } from '@air/utils-tailwind';

import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { StepHeader } from '~/components/WorkspaceOnboardingForm/StepHeader';
import { getTotalFormSteps } from '~/components/WorkspaceOnboardingForm/utils';
import { WorkspaceOnboardingCardLayout } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingCardLayout';
import { WorkspaceOnboardingContainer } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingContainer';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { splitFilesByFolder, useQueuePrivateUploads } from '~/hooks/useQueuePrivateUploads';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useUploadValidation } from '~/hooks/useUploadValidation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

type UploadInputType = 'files' | 'folders';

export const WorkspaceOnboardingUploadPrompt = memo(() => {
  const { trackViewedOnboardingUpload } = useTrackViewedOnboardingUpload();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: workspaces } = useWorkspaces();
  const { validateUpload } = useUploadValidation({ workspaceId });
  const { queuePrivateUploads } = useQueuePrivateUploads();
  const [inputType, setInputType] = useState<UploadInputType | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const clearShowFirstUploadPrompt = useOnboardingPreferencesStore((state) => state.clearShowFirstUploadPrompt);
  const { trackCompletedOnboardingUpload } = useTrackCompletedOnboardingUpload();
  const { data: account } = useAccount();
  const { createNewBoard } = useCreateNewBoard();
  const { goToBoardPage } = useGoToBoardPage();
  const { skipOnboardingUpload } = useFeatureFlags();
  const { onboardingTeamInviteStepEnabled } = useTemporaryFeatureFlags();

  const isFirstWorkspace = workspaces && workspaces.length === 1;
  const totalSteps = getTotalFormSteps(
    !!isFirstWorkspace,
    Boolean(isFirstWorkspace && onboardingTeamInviteStepEnabled),
  );

  useEffect(() => {
    trackViewedOnboardingUpload();
  }, [trackViewedOnboardingUpload]);

  const handleDrop = useCallback(
    async (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      setInputType(null);

      if (!workspaceId) {
        return;
      }

      const validatedFiles = await validateUpload(acceptedFiles);

      if (!validatedFiles) {
        return;
      }

      const { files, emptyFiles, filesWithNoExtension } = validatedFiles;
      const [filesInFolders, filesNotInFolders] = splitFilesByFolder(files);
      const topLevelFilesToUpload = filesNotInFolders.map((file) => ({ file }));

      let board: Board | undefined;

      try {
        board = await createNewBoard({
          board: { title: `${account?.firstName || 'User'}'s first board` },
          trackLocation: 'workspace-onboarding',
          shouldFollow: true,
        });
      } catch (_error) {
        // do nothing
      }

      clearShowFirstUploadPrompt(workspaceId);
      if (board) {
        await goToBoardPage({ board, method: 'replace' });
      }
      setIsDraggingOver(false);

      queuePrivateUploads({
        filesInFolders,
        topLevelFilesToUpload,
        rejectedFiles: fileRejections.map(({ file }) => file),
        emptyFiles,
        boardToUpload: board,
        filesWithNoExtension,
      });
      trackCompletedOnboardingUpload();
    },
    [
      workspaceId,
      validateUpload,
      queuePrivateUploads,
      clearShowFirstUploadPrompt,
      goToBoardPage,
      account?.firstName,
      createNewBoard,
      trackCompletedOnboardingUpload,
    ],
  );

  const handleFileDialogCancel = useCallback(() => {
    setInputType(null);
  }, []);

  const handleDragEnter = useCallback(() => {
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onFileDialogCancel: handleFileDialogCancel,
    useFsAccessApi: false,
  });

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (inputType && openRef.current) {
      openRef.current();
    }
  }, [inputType]);

  const handleSelectFilesClick = useCallback(() => {
    setInputType('files');
  }, []);

  const handleSelectFoldersClick = useCallback(() => {
    setInputType('folders');
  }, []);

  const handleSkipClick = useCallback(() => {
    if (workspaceId) {
      clearShowFirstUploadPrompt(workspaceId);
    }
  }, [clearShowFirstUploadPrompt, workspaceId]);

  return (
    <WorkspaceOnboardingContainer>
      <WorkspaceOnboardingCardLayout currentStep={totalSteps - 1} totalSteps={totalSteps}>
        <form className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex min-h-0 flex-1 flex-col gap-6" data-testid="WORKSPACE_ONBOARDING_UPLOAD_PROMPT">
            <StepHeader
              title="Upload your first file."
              description="Your workspace comes to life once you add an asset. Drop in a file to see what Air can do."
            />
            <div
              {...getRootProps()}
              data-testid="WORKSPACE_ONBOARDING_UPLOAD_DROPZONE"
              className={cn(
                'flex min-h-0 flex-1 flex-col items-center justify-center gap-5 rounded-12 border px-4 py-4 transition-colors',
                isDraggingOver
                  ? 'border-2 border-altitude-9 border-solid bg-altitude-2'
                  : 'border-grey-7 border-dashed bg-grey-1',
              )}
            >
              {inputType === 'folders' ? (
                <input
                  {...getInputProps()}
                  // @ts-expect-error - https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory - "directory" is a nonstandard prop but is the only way to do it 💁
                  directory=""
                  mozdirectory=""
                  type="file"
                  webkitdirectory=""
                  data-testid="WORKSPACE_ONBOARDING_UPLOAD_FOLDER_INPUT"
                />
              ) : (
                <input {...getInputProps()} data-testid="WORKSPACE_ONBOARDING_UPLOAD_FILE_INPUT" />
              )}
              <Uploads className="size-5 text-grey-12" />
              <div className="flex flex-col items-center gap-1 text-center">
                <div className="font-medium text-16 text-grey-12">Drag and drop your files here</div>
                <div className="text-12 text-grey-11">Add your creative assets here</div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button appearance="filled" color="grey" type="button" size="small" onClick={handleSelectFilesClick}>
                  Select files
                </Button>
                <Button appearance="filled" color="grey" type="button" size="small" onClick={handleSelectFoldersClick}>
                  Select folders
                </Button>
              </div>
            </div>
          </div>
        </form>
        {skipOnboardingUpload && (
          <div className="flex justify-end">
            <Button
              appearance="ghost"
              color="grey"
              type="button"
              size="medium"
              onClick={handleSkipClick}
              data-testid="WORKSPACE_ONBOARDING_UPLOAD_SKIP"
            >
              Skip
            </Button>
          </div>
        )}
      </WorkspaceOnboardingCardLayout>
    </WorkspaceOnboardingContainer>
  );
});

WorkspaceOnboardingUploadPrompt.displayName = 'WorkspaceOnboardingUploadPrompt';
